
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Snackbar,
    Stack,
    TextField,
    Typography,
    InputAdornment,
    IconButton,
} from '@mui/material';

import {
    DataGrid,
    GridActionsCellItem,
} from '@mui/x-data-grid';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
//import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import {
    getDispatches,
    createDispatch,
    updateDispatch,
    deleteDispatch,
} from '../../services/assemblyService';

import config from '../../config/config';
import { getAuthToken } from '../../utils/token';

const emptyForm = {
    po_id: '',
    product_id: '',
    dispatch_date: new Date().toISOString().slice(0, 10),
    quantity_dispatched: '',
    remark: '',
    invoice_no: '',
    record_status: 1,
};

const DispatchManagement = () => {
    // --------------------------------------------------
    // State
    // --------------------------------------------------
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(false);

    const [productOptions, setProductOptions] = useState([]);
    const [listPo, setListPo] = useState([]);

    const [searchText, setSearchText] = useState('');

    const [openForm, setOpenForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ ...emptyForm });
    const [errors, setErrors] = useState({});
    const [deleteId, setDeleteId] = useState(null);

    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // --------------------------------------------------
    // Notification
    // --------------------------------------------------
    const notify = useCallback((message, severity = 'success') => {
        setNotification({
            open: true,
            message,
            severity,
        });
    }, []);

    const closeNotification = () => {
        setNotification((previous) => ({
            ...previous,
            open: false,
        }));
    };

    // --------------------------------------------------
    // Load dispatch records
    // --------------------------------------------------
    const loadDispatches = useCallback(async () => {
        setLoading(true);

        try {
            const response = await getDispatches();

            const records = Array.isArray(response)
                ? response
                : Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response?.data?.data)
                        ? response.data.data
                        : [];

            setRows(records);
        } catch (error) {
            notify(
                error.response?.data?.message ||
                'Unable to load dispatch records.',
                'error'
            );
        } finally {
            setLoading(false);
        }
    }, [notify]);

    // --------------------------------------------------
    // Load products and purchase orders
    // --------------------------------------------------
    const fetchDropdownOptions = useCallback(async () => {
        const token = getAuthToken();

        if (!token) {
            notify(
                'Authentication token not found. Please log in.',
                'error'
            );
            return;
        }

        setOptionsLoading(true);

        try {
            const headers = {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            };

            const [productResponse, poResponse] = await Promise.all([
                axios.get(`${config.API}/product/list`, { headers }),
                axios.get(`${config.API}/purchaseorder/list`, { headers }),
            ]);

            const products =
                productResponse?.data?.products ??
                productResponse?.data?.data ??
                [];

            const purchaseOrders =
                poResponse?.data?.data ??
                poResponse?.data?.purchase_orders ??
                [];

            setProductOptions(
                products.map((product) => ({
                    value: product.id,
                    label: product.name ?? product.product_name ?? '',
                }))
            );

            setListPo(
                purchaseOrders.map((po) => ({
                    value: po.id,
                    label: po.po_number ?? po.order_id ?? String(po.id),
                }))
            );
        } catch (error) {
            console.error('Error loading dropdown options:', error);

            notify(
                error.response?.data?.message ||
                'Unable to load products or purchase orders.',
                'error'
            );
        } finally {
            setOptionsLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        loadDispatches();
        fetchDropdownOptions();
    }, [loadDispatches, fetchDropdownOptions]);

    // --------------------------------------------------
    // Create dispatch
    // --------------------------------------------------
    const openCreateDialog = () => {
        setEditingId(null);
        setForm({ ...emptyForm });
        setErrors({});
        setOpenForm(true);
    };

    // --------------------------------------------------
    // Edit dispatch
    // --------------------------------------------------
    const openEditDialog = (row) => {
        setEditingId(row.id);

        setForm({
            po_id: row.po_id ?? '',
            product_id: row.product_id ?? '',
            dispatch_date: row.dispatch_date
                ? String(row.dispatch_date).slice(0, 10)
                : '',
            quantity_dispatched: row.quantity_dispatched ?? '',
            remark: row.remark ?? '',
            invoice_no: row.invoice_no ?? '',
            record_status: Number(row.record_status ?? 1),
        });

        setErrors({});
        setOpenForm(true);
    };

    const closeForm = () => {
        if (saving) return;

        setOpenForm(false);
        setErrors({});
    };

    // --------------------------------------------------
    // Input changes
    // --------------------------------------------------
    const handleChange = (event) => {
        const { name, value } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));

        setErrors((previous) => ({
            ...previous,
            [name]: '',
        }));
    };

    // --------------------------------------------------
    // Validation
    // --------------------------------------------------
    const validateForm = () => {
        const nextErrors = {};

        if (!form.po_id || Number(form.po_id) <= 0) {
            nextErrors.po_id = 'Please select a purchase order.';
        }

        if (!form.product_id || Number(form.product_id) <= 0) {
            nextErrors.product_id = 'Please select a product.';
        }

        if (!form.dispatch_date) {
            nextErrors.dispatch_date = 'Dispatch date is required.';
        }

        if (
            form.quantity_dispatched === '' ||
            !Number.isFinite(Number(form.quantity_dispatched)) ||
            Number(form.quantity_dispatched) <= 0
        ) {
            nextErrors.quantity_dispatched =
                'Quantity must be greater than zero.';
        }

        setErrors(nextErrors);

        return Object.keys(nextErrors).length === 0;
    };

    // --------------------------------------------------
    // Save / Update dispatch
    // --------------------------------------------------
    const handleSave = async (event) => {
        event.preventDefault();

        if (!validateForm()) return;

        const payload = {
            invoice_no: String(form.invoice_no).trim(),
            po_id: Number(form.po_id),
            product_id: Number(form.product_id),
            dispatch_date: form.dispatch_date,
            quantity_dispatched: Number(form.quantity_dispatched),
            remark: String(form.remark ?? '').trim(),
            record_status: Number(form.record_status),
        };

        setSaving(true);

        try {
            const response =
                editingId !== null
                    ? await updateDispatch(editingId, payload)
                    : await createDispatch(payload);

            // Handle services returning success: false.
            if (
                response?.success === false ||
                response?.data?.success === false
            ) {
                const result =
                    response?.data?.success === false
                        ? response.data
                        : response;

                const stock = result?.data;

                if (stock?.available_stock !== undefined) {
                    notify(
                        `${result.message || 'Insufficient stock.'} ` +
                        `Available: ${stock.available_stock}, ` +
                        `Requested: ${stock.requested_quantity}, ` +
                        `Shortage: ${stock.shortage}`,
                        'error'
                    );
                } else {
                    notify(
                        result?.message || 'Unable to save dispatch.',
                        'error'
                    );
                }

                return;
            }

            notify(
                editingId !== null
                    ? 'Dispatch record updated successfully.'
                    : 'Dispatch record created successfully.'
            );

            setOpenForm(false);
            await loadDispatches();
        } catch (error) {
            const responseData = error.response?.data;

            if (responseData?.errors) {
                const validationErrors = {};

                Object.entries(responseData.errors).forEach(
                    ([field, messages]) => {
                        validationErrors[field] = Array.isArray(messages)
                            ? messages[0]
                            : String(messages);
                    }
                );

                setErrors(validationErrors);
            }

            const stock =
                responseData?.data?.available_stock !== undefined
                    ? responseData.data
                    : responseData?.available_stock !== undefined
                        ? responseData
                        : null;

            if (stock) {
                notify(
                    `${responseData?.message || 'Insufficient stock.'} ` +
                    `Available: ${stock.available_stock}, ` +
                    `Requested: ${stock.requested_quantity}, ` +
                    `Shortage: ${stock.shortage}`,
                    'error'
                );
            } else {
                notify(
                    responseData?.message ||
                    'Unable to save dispatch record.',
                    'error'
                );
            }
        } finally {
            setSaving(false);
        }
    };

    // --------------------------------------------------
    // Delete dispatch
    // --------------------------------------------------
    const handleDelete = async () => {
        if (deleteId === null) return;

        setSaving(true);

        try {
            await deleteDispatch(deleteId);

            setDeleteId(null);
            notify('Dispatch record deleted successfully.');

            await loadDispatches();
        } catch (error) {
            notify(
                error.response?.data?.message ||
                'Unable to delete dispatch record.',
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    // --------------------------------------------------
    // Search helper values
    // --------------------------------------------------
    const getPoNumber = useCallback(
        (row) =>
            row.po_number ??
            listPo.find(
                (po) => String(po.value) === String(row.po_id)
            )?.label ??
            row.po_id ??
            '',
        [listPo]
    );

    const getProductName = useCallback(
        (row) =>
            row.product_name ??
            row.name ??
            productOptions.find(
                (product) =>
                    String(product.value) === String(row.product_id)
            )?.label ??
            row.product_id ??
            '',
        [productOptions]
    );

    // --------------------------------------------------
    // Global search filter
    // --------------------------------------------------
    const filteredRows = useMemo(() => {
        const query = searchText.trim().toLowerCase();

        if (!query) return rows;

        return rows.filter((row) => {
            const status =
                Number(row.record_status) === 1
                    ? 'Active'
                    : 'Inactive';

            const searchableValues = [
                row.id,
                row.invoice_no,
                getPoNumber(row),
                getProductName(row),
                row.dispatch_date
                    ? String(row.dispatch_date).slice(0, 10)
                    : '',
                row.quantity_dispatched,
                row.remark,
                status,
            ];

            return searchableValues.some((value) =>
                String(value ?? '').toLowerCase().includes(query)
            );
        });
    }, [rows, searchText, getPoNumber, getProductName]);

    // --------------------------------------------------
    // DataGrid columns
    // --------------------------------------------------
    const columns = useMemo(
        () => [
            {
                field: 'id',
                headerName: 'ID',
                width: 50,
                type: 'number',
            },
            {
                field: 'invoice_no',
                headerName: 'Invoice No.',
                minWidth: 100,
                flex: 1,
            },
            {
                field: 'po_number',
                headerName: 'PO Number',
                minWidth: 170,
                flex: 1,
                valueGetter: (value, row) => getPoNumber(row),
            },
            {
                field: 'product_name',
                headerName: 'Product',
                minWidth: 180,
                flex: 1.5,
                valueGetter: (value, row) => getProductName(row),
            },
            {
                field: 'dispatch_date',
                headerName: 'Dispatch Date',
                minWidth: 110,
                valueFormatter: (value) =>
                    value ? String(value).slice(0, 10) : '',
            },
            {
                field: 'quantity_dispatched',
                headerName: 'Dispatched Qty',
                type: 'number',
                minWidth: 110,
            },
            {
                field: 'remark',
                headerName: 'Remark',
                minWidth: 110,
                flex: 1,
            },

            {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                width: 100,
                getActions: ({ row }) => [
                    <GridActionsCellItem
                        key="edit"
                        icon={<EditIcon />}
                        label="Edit"
                        onClick={() => openEditDialog(row)}
                    />

                ],
            },
        ],
        [getPoNumber, getProductName]
    );

    // --------------------------------------------------
    // Render
    // --------------------------------------------------
    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1}
                sx={{ mb: 1 }}
            >
                <Typography variant="h5" fontWeight={600}>
                    Dispatch
                </Typography>

                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >


                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={openCreateDialog}
                        sx={{
                            height: 36,
                            whiteSpace: 'nowrap',
                            minWidth: 130,
                        }}
                    >
                        Add Dispatch
                    </Button>
                    <TextField
                        size="small"
                        label="Search Dispatch"
                        placeholder="Invoice, PO, product..."
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        sx={{
                            width: { xs: '100%', sm: 260, md: 300 },
                            '& .MuiInputBase-root': {
                                height: 36,
                                fontSize: '0.875rem',
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '0.875rem',
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: searchText ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="Clear search"
                                        onClick={() => setSearchText('')}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </Stack>
            </Stack>

            {/* Search and DataGrid */}

            <Box sx={{ width: '100%', height: 400, minHeight: 400 }}>
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    loading={loading}
                    getRowId={(row) => row.id}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                page: 0,
                                pageSize: 5,
                            },
                        },
                    }}
                    disableRowSelectionOnClick
                    localeText={{
                        noRowsLabel: 'No dispatch records found',
                    }}
                    sx={{
                        border: 1,
                        borderColor: 'divider',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: 'action.hover',
                            fontWeight: 600,
                        },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
                        {
                            outline: 'none',
                        },
                    }}
                />
            </Box>


            {/* Create / Edit Dialog */}
            <Dialog
                open={openForm}
                onClose={closeForm}
                fullWidth
                maxWidth="sm"
            >
                <Box component="form" onSubmit={handleSave}>
                    <DialogTitle>
                        {editingId !== null
                            ? 'Update Dispatch'
                            : 'Create Dispatch'}
                    </DialogTitle>

                    <DialogContent>
                        <Stack spacing={1} sx={{ pt: 1 }}>
                            <TextField
                                label="Invoice Number"
                                name="invoice_no"
                                value={form.invoice_no}
                                onChange={handleChange}
                                error={Boolean(errors.invoice_no)}
                                helperText={errors.invoice_no}
                                fullWidth
                            />

                            {/* Purchase Order */}
                            <Autocomplete
                                options={listPo}
                                loading={optionsLoading}
                                value={
                                    listPo.find(
                                        (option) =>
                                            String(option.value) ===
                                            String(form.po_id)
                                    ) || null
                                }
                                getOptionLabel={(option) =>
                                    String(option.label ?? '')
                                }
                                isOptionEqualToValue={(option, value) =>
                                    String(option.value) ===
                                    String(value.value)
                                }
                                onChange={(_, selectedOption) => {
                                    setForm((previous) => ({
                                        ...previous,
                                        po_id: selectedOption?.value ?? '',
                                    }));

                                    setErrors((previous) => ({
                                        ...previous,
                                        po_id: '',
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Purchase Order"
                                        placeholder="Search PO number..."
                                        required
                                        error={Boolean(errors.po_id)}
                                        helperText={errors.po_id}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {optionsLoading && (
                                                        <CircularProgress
                                                            size={18}
                                                            sx={{ mr: 1 }}
                                                        />
                                                    )}
                                                    {params.InputProps?.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />

                            {/* Product */}
                            <Autocomplete
                                options={productOptions}
                                loading={optionsLoading}
                                value={
                                    productOptions.find(
                                        (option) =>
                                            String(option.value) ===
                                            String(form.product_id)
                                    ) || null
                                }
                                getOptionLabel={(option) =>
                                    String(option.label ?? '')
                                }
                                isOptionEqualToValue={(option, value) =>
                                    String(option.value) ===
                                    String(value.value)
                                }
                                onChange={(_, selectedOption) => {
                                    setForm((previous) => ({
                                        ...previous,
                                        product_id:
                                            selectedOption?.value ?? '',
                                    }));

                                    setErrors((previous) => ({
                                        ...previous,
                                        product_id: '',
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Product"
                                        placeholder="Search product..."
                                        required
                                        error={Boolean(errors.product_id)}
                                        helperText={errors.product_id}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {optionsLoading && (
                                                        <CircularProgress
                                                            size={18}
                                                            sx={{ mr: 1 }}
                                                        />
                                                    )}
                                                    {params.InputProps?.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />

                            <TextField
                                label="Dispatch Date"
                                name="dispatch_date"
                                type="date"
                                value={form.dispatch_date}
                                onChange={handleChange}
                                error={Boolean(errors.dispatch_date)}
                                helperText={errors.dispatch_date}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                                required
                                fullWidth
                            />

                            <TextField
                                label="Dispatched Quantity"
                                name="quantity_dispatched"
                                type="number"
                                value={form.quantity_dispatched}
                                onChange={handleChange}
                                error={Boolean(errors.quantity_dispatched)}
                                helperText={errors.quantity_dispatched}
                                inputProps={{
                                    min: 0.01,
                                    step: 'any',
                                }}
                                required
                                fullWidth
                            />

                            <TextField
                                label="Remark"
                                name="remark"
                                value={form.remark}
                                onChange={handleChange}
                                multiline
                                minRows={2}
                                fullWidth
                            />

                            <TextField
                                select
                                label="Status"
                                name="record_status"
                                value={form.record_status}
                                onChange={handleChange}
                                fullWidth
                            >
                                <MenuItem value={1}>Active</MenuItem>
                                <MenuItem value={0}>Inactive</MenuItem>
                            </TextField>
                        </Stack>
                    </DialogContent>

                    <DialogActions sx={{ p: 2 }}>
                        <Button
                            onClick={closeForm}
                            disabled={saving}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={saving}
                        >
                            {saving ? (
                                <CircularProgress size={22} />
                            ) : editingId !== null ? (
                                'Update'
                            ) : (
                                'Save'
                            )}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog
                open={deleteId !== null}
                onClose={() => {
                    if (!saving) setDeleteId(null);
                }}
            >
                <DialogTitle>Delete Dispatch</DialogTitle>

                <DialogContent>
                    Are you sure you want to delete this dispatch record?
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setDeleteId(null)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={saving}
                    >
                        {saving ? (
                            <CircularProgress size={20} />
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notifications */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={closeNotification}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <Alert
                    severity={notification.severity}
                    variant="filled"
                    onClose={closeNotification}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DispatchManagement;
